import { assertNever } from "@impero-com/common/utils/obj";

import { PageState, DashboardFilters } from "api/generated/model";
import { RISK_EXTRACTOR_KEYS } from "org/risks/types";
import { USER_EXTRACTOR_KEYS } from "org/user/list/extractorKeys";

import { CONTROL_PROGRAMS_ALL_COLUMNS } from "./convert/controlPrograms";
import { RiskDirectoryPageStateWithType } from "./convert/risks/riskDirectories";
import { RiskMapPageStateWithType } from "./convert/risks/riskMaps";
import { USER_GROUPS_ALL_COLUMNS } from "./convert/userGroups";
import { ALL_COLUMNS as ALL_COLUMNS_USERS } from "./convert/users";
import { PageType } from "./types";
import { getVersionForPageState } from "./utils";

const DEFAULT_CONTROLS_ALL_PAGE_STATE: PageState = {
    type: "ControlsAll",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "schedulingType",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsAll"),
};

const DEFAULT_CONTROLS_WEEKLY_PAGE_STATE: PageState = {
    type: "ControlsWeekly",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "recurrenceWeekly",
        "notification",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsWeekly"),
};

const DEFAULT_CONTROLS_MONTHLY_PAGE_STATE: PageState = {
    type: "ControlsMonthly",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "recurrenceMonthly",
        "notification",
        "scheduleMonthly",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsMonthly"),
};

const DEFAULT_CONTROLS_TWO_MONTHS_PAGE_STATE: PageState = {
    type: "ControlsTwoMonths",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "recurrenceEveryTwoMonths",
        "notification",
        "scheduleEveryTwoMonths",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsTwoMonths"),
};

const DEFAULT_CONTROLS_QUARTERLY_PAGE_STATE: PageState = {
    type: "ControlsQuarterly",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "recurrenceQuarterly",
        "notification",
        "scheduleQuarterly",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsQuarterly"),
};

const DEFAULT_CONTROLS_HALF_YEARLY_PAGE_STATE: PageState = {
    type: "ControlsHalfYearly",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "recurrenceHalfYearly",
        "notification",
        "scheduleHalfYearly",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsHalfYearly"),
};

const DEFAULT_CONTROLS_YEARLY_PAGE_STATE: PageState = {
    type: "ControlsYearly",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "recurrenceYearly",
        "notification",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsYearly"),
};

const DEFAULT_CONTROLS_MANUAL_PAGE_STATE: PageState = {
    type: "ControlsManual",
    filters: {
        common: {},
        specific: {},
    },
    columns: [
        "name",
        "program",
        "dueDateManual",
        "assignment",
        "tags",
        "accessRight",
        "controlResultCount",
    ],
    version: getVersionForPageState("ControlsManual"),
};

const DEFAULT_USER_GROUPS_PAGE_STATE: PageState = {
    type: "UserGroups",
    filters: {},
    columns: USER_GROUPS_ALL_COLUMNS,
    version: getVersionForPageState("UserGroups"),
};

const DEFAULT_CONTROL_PROGRAMS_PAGE_STATE: PageState = {
    type: "ControlPrograms",
    filters: {},
    columns: CONTROL_PROGRAMS_ALL_COLUMNS,
    version: getVersionForPageState("ControlPrograms"),
};

const DEFAULT_CONTROL_PROGRAM_PAGE_STATE: PageState = {
    type: "ControlProgram",
    mode: "grid",
    userTagCategoryFilter: null,
    version: getVersionForPageState("ControlProgram"),
};

const DEFAULT_USER_MANAGEMENT_PAGE_STATE: PageState = {
    type: "Users",
    filters: {},
    // Provision status is hidden by default
    columns: ALL_COLUMNS_USERS.filter(
        (col) => col !== USER_EXTRACTOR_KEYS.PROVISION_STATUS
    ),
    version: getVersionForPageState("Users"),
};

const DEFAULT_ACTIVITIES_PAGE_STATE: PageState = {
    type: "Activities",
    filters: {},
    columns: [
        { type: "Static", value: "controlProgram" },
        { type: "Static", value: "title" },
        { type: "Static", value: "coveredPeriod" },
        { type: "Static", value: "tagCategories" },
        { type: "Static", value: "dueDate" },
        { type: "Static", value: "completedDate" },
        { type: "Static", value: "responsible" },
        { type: "Static", value: "reviewer" },
        { type: "Static", value: "workflowStatus" },
    ],
    version: getVersionForPageState("Activities"),
};

const DEFAULT_DATA_SHEET_REPORT_PAGE_STATE: PageState = {
    type: "DataSheetReport",
    filters: {},
    columns: [
        { type: "Static", value: "title" },
        { type: "Static", value: "dueDate" },
        { type: "Static", value: "completedDate" },
        { type: "Static", value: "reviewedDate" },
        { type: "Static", value: "completedAndReviewedDate" },
        { type: "Static", value: "workflowStatus" },
        { type: "Static", value: "verified" },
        { type: "Static", value: "verificationDate" },
        { type: "Static", value: "verifiedBy" },
    ],
    version: getVersionForPageState("DataSheetReport"),
};

export const DEFAULT_RISK_MAP_PAGE_STATE: RiskMapPageStateWithType = {
    type: "RiskMap",
    filtersAndColumns: {
        filters: {},
        // Matches DEFAULT_VISIBLE columns in buildMapColumnBuilders() in frontend/spa/src/org/risks/content/map/index.tsx
        columns: [
            { type: "Static", value: RISK_EXTRACTOR_KEYS.PATH },
            { type: "Static", value: RISK_EXTRACTOR_KEYS.NAME },
            { type: "Static", value: RISK_EXTRACTOR_KEYS.DESCRIPTION },
            { type: "Static", value: RISK_EXTRACTOR_KEYS.CONTROLS },
            { type: "Static", value: RISK_EXTRACTOR_KEYS.ENTITIES },
            { type: "Static", value: RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS },
        ],
        version: getVersionForPageState("RiskMap"),
    },
    hideHeatmap: false,
    showResidualHeatmap: false,
    showTableView: false,
    focusFilterCoords: [],
};

export const DEFAULT_RISK_DIRECTORY_PAGE_STATE: RiskDirectoryPageStateWithType =
    {
        type: "RiskDirectory",
        filtersAndColumns: {
            filters: {},
            // Matches DEFAULT_VISIBLE columns in buildMapColumnBuilders() in frontend/spa/src/org/risks/content/map/index.tsx
            columns: [
                { type: "Static", value: RISK_EXTRACTOR_KEYS.PATH },
                { type: "Static", value: RISK_EXTRACTOR_KEYS.NAME },
                { type: "Static", value: RISK_EXTRACTOR_KEYS.DESCRIPTION },
                { type: "Static", value: RISK_EXTRACTOR_KEYS.CONTROLS },
                { type: "Static", value: RISK_EXTRACTOR_KEYS.ENTITIES },
                {
                    type: "Static",
                    value: RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS,
                },
            ],
            version: getVersionForPageState("RiskDirectory"),
        },
        showTableView: false,
    };

const DEFAULT_TAG_MANAGEMENT_PAGE_STATE: PageState = {
    type: "Tags",
    filters: {},
    columns: ["categoryName", "categoryCount"],
    version: 0,
};

const DEFAULT_REPORTS_ALL_PAGE_STATE: PageState = {
    type: "ReportsAll",
    version: getVersionForPageState("ReportsAll"),
    columns: [
        "name",
        "reportType",
        "description",
        "pushReportsCount",
        "accessRights",
        "actions",
    ],
    filters: {},
    leftSideMenuCollapsed: false,
};

const DEFAULT_REPORTS_STATUS_PAGE_STATE: PageState = {
    type: "ReportsStatus",
    version: getVersionForPageState("ReportsStatus"),
    columns: [
        "name",
        "description",
        "pushReportsCount",
        "accessRights",
        "actions",
    ],
    filters: {},
    leftSideMenuCollapsed: false,
};

const DEFAULT_REPORTS_CLASSIFICATION_PAGE_STATE: PageState = {
    type: "ReportsClassification",
    version: 0,
    columns: [
        "name",
        "classification",
        "description",
        "pushReportsCount",
        "accessRights",
        "actions",
    ],
    filters: {},
    leftSideMenuCollapsed: false,
};

const DEFAULT_REPORTS_WORKLOAD_PAGE_STATE: PageState = {
    type: "ReportsWorkload",
    version: 0,
    columns: [
        "name",
        "description",
        "pushReportsCount",
        "accessRights",
        "actions",
    ],
    filters: {},
    leftSideMenuCollapsed: false,
};

const DEFAULT_REPORTS_DATASHEET_PAGE_STATE: PageState = {
    type: "ReportsDatasheet",
    version: 0,
    columns: ["datasheet", "program", "archived"],
    filters: {},
    leftSideMenuCollapsed: false,
};

export const DEFAULT_DASHBOARD_FILTERS: DashboardFilters = {
    timePeriod: { type: "CurrentMonth" },
    showInactivePrograms: false,
    showArchivedActivities: false,
    tags: [],
    controlPrograms: [],
    entities: [],
};

const DEFAULT_DASHBOARD_PAGE_STATE: PageState = {
    type: "Dashboard",
    filters: DEFAULT_DASHBOARD_FILTERS,
    version: getVersionForPageState("Dashboard"),
};

function getDefaultPageState(pageType: PageType): PageState {
    switch (pageType) {
        case "ControlsAll":
            return DEFAULT_CONTROLS_ALL_PAGE_STATE;
        case "ControlsWeekly":
            return DEFAULT_CONTROLS_WEEKLY_PAGE_STATE;
        case "ControlsMonthly":
            return DEFAULT_CONTROLS_MONTHLY_PAGE_STATE;
        case "ControlsTwoMonths":
            return DEFAULT_CONTROLS_TWO_MONTHS_PAGE_STATE;
        case "ControlsQuarterly":
            return DEFAULT_CONTROLS_QUARTERLY_PAGE_STATE;
        case "ControlsHalfYearly":
            return DEFAULT_CONTROLS_HALF_YEARLY_PAGE_STATE;
        case "ControlsYearly":
            return DEFAULT_CONTROLS_YEARLY_PAGE_STATE;
        case "ControlsManual":
            return DEFAULT_CONTROLS_MANUAL_PAGE_STATE;
        case "UserGroups":
            return DEFAULT_USER_GROUPS_PAGE_STATE;
        case "Users":
            return DEFAULT_USER_MANAGEMENT_PAGE_STATE;
        case "ControlPrograms":
            return DEFAULT_CONTROL_PROGRAMS_PAGE_STATE;
        case "Activities":
            return DEFAULT_ACTIVITIES_PAGE_STATE;
        case "DataSheetReport":
            return DEFAULT_DATA_SHEET_REPORT_PAGE_STATE;
        case "RiskMap":
            return DEFAULT_RISK_MAP_PAGE_STATE;
        case "RiskDirectory":
            return DEFAULT_RISK_DIRECTORY_PAGE_STATE;
        case "Tags":
            return DEFAULT_TAG_MANAGEMENT_PAGE_STATE;
        case "ControlProgram":
            return DEFAULT_CONTROL_PROGRAM_PAGE_STATE;
        case "ReportsAll":
            return DEFAULT_REPORTS_ALL_PAGE_STATE;
        case "ReportsStatus":
            return DEFAULT_REPORTS_STATUS_PAGE_STATE;
        case "ReportsClassification":
            return DEFAULT_REPORTS_CLASSIFICATION_PAGE_STATE;
        case "ReportsDatasheet":
            return DEFAULT_REPORTS_DATASHEET_PAGE_STATE;
        case "ReportsWorkload":
            return DEFAULT_REPORTS_WORKLOAD_PAGE_STATE;
        case "Dashboard":
            return DEFAULT_DASHBOARD_PAGE_STATE;
        default:
            return assertNever(pageType);
    }
}

export { getDefaultPageState };
